import { HttpClient } from '@angular/common/http';
import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  StateUpdatedEvent
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { ToastrService } from 'ngx-toastr';
import { LadingStatusModalComponent } from 'src/app/lading-status-modal/lading-status-modal.component';
import { storeReportDefaultGridState } from '../default-grid-states/store-report-default-grid-state';
import { StoreReport } from '../models/store-report.model';
import { UserGridStateService } from '../user-grid-state.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  AgCharts,
  AgCartesianChartOptions,
  AgBarSeriesOptions
} from 'ag-charts-community';
import { MenuData } from '../models/browser-menu-data.model';

type StoreReportExtended = StoreReport & {
  sort_week: string;
  chart_week: string;
  distribution_center_reference?: string | null;
  pool_point_reference_code?: string | null;
  lading_uuid: string;
};

@Component({
  selector: 'app-store-reports-client',
  templateUrl: './store-reports-client.component.html',
  styleUrls: ['./store-reports-client.component.scss']
})
export class StoreReportsClientComponent implements OnInit {

  private apiUrl = '/api/store_reports/store_reports_client.php';
  private isAllan = false;

  public theme: string = 'ag-theme-ulaap';
  public gridOptions: GridOptions = {
    enableCharts: true,
    enableRangeSelection: true
  };

  private gridApi!: GridApi<StoreReport>;
  public rowData: StoreReportExtended[] = [];
  public initialState = JSON.parse(JSON.stringify(storeReportDefaultGridState));
  public gridReady = false;

  public defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset']
    },
    autoHeight: true,
    resizable: true
  };

  private quickFilter$ = new Subject<string>();

  public columnDefs: ColDef[] = [
    {
      headerName: '',
      editable: false,
      width: 48,
      filter: false,
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: ICellRendererParams<StoreReportExtended>) => {
        const button = document.createElement('button');
        button.className = 'row-action';
        const icon = document.createElement('i');
        icon.classList.add('fas', 'fa-edit');
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => this.openStatusModal(params));
        });
        return button;
      }
    },
    { field: 'trip_number', headerName: 'Batch ID' },
    { field: 'asn_number', headerName: 'Master BOL' },
    { field: 'customer_name', headerName: 'Customer' },
    { field: 'alternate_reference', headerName: 'FBI #', width: 110 },
    { field: 'store_number', headerName: 'Store' },
    { field: 'distribution_center_reference', headerName: 'DC ID', width: 120 },
    { field: 'pool_point_reference_code', headerName: 'Pool ID', width: 120 },

    {
      field: 'week_no',
      headerName: 'Week No.',
      filter: 'agNumberColumnFilter',
      comparator: (a, b, nodeA, nodeB) => {
        const va = (nodeA?.data?.sort_week ?? '') as string;
        const vb = (nodeB?.data?.sort_week ?? '') as string;
        return va.localeCompare(vb);
      },
      valueGetter: p => {
        const d = this.pickRefDate(p.data);
        return p.data?.week_no ?? this.getWeekNumber(d);
      }
    },

    { field: 'external_bill_of_lading', headerName: 'BOL #' },
    { field: 'total_cartons', headerName: 'Carton Count', filter: 'agNumberColumnFilter' },
    { field: 'package_count', headerName: 'Pallets', filter: 'agNumberColumnFilter' },
    { field: 'reported_weight', headerName: 'Reported Weight', filter: 'agNumberColumnFilter' },

    {
      field: 'load_start',
      headerName: 'Shipped Date',
      filter: 'agDateColumnFilter',
      valueGetter: p => this.formatDateString(p.data?.load_start)
    },

    {
      field: 'local_time',
      headerName: 'Actual Delivery Date',
      filter: 'agDateColumnFilter',
      valueGetter: p => this.formatDateString(p.data?.local_time)
    },

    {
      field: 'local_time',
      headerName: 'Actual Delivery Time',
      filter: false,
      valueGetter: p => this.formatTimeString(p.data?.local_time)
    },

    {
      headerName: 'Appt Day of Week',
      filter: false,
      valueGetter: p => {
        const dateStr = p.data?.appointment_date;
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'long' });
      }
    },

    {
      headerName: 'Actual Day of Week',
      field: 'local_time',
      filter: false,
      cellRenderer: (params: ICellRendererParams<StoreReportExtended>) => {
        const apptStr = params.data?.appointment_date;
        const actualStr = params.value;
        if (!apptStr || !actualStr) return '';

        const appt = new Date(apptStr);
        const actual = new Date(actualStr);
        if (isNaN(appt.getTime()) || isNaN(actual.getTime())) return '';

        const apptDay = appt.toLocaleDateString('en-US', { weekday: 'long' });
        const actualDay = actual.toLocaleDateString('en-US', { weekday: 'long' });

        const apptDate = new Date(appt.getFullYear(), appt.getMonth(), appt.getDate());
        const actualDate = new Date(actual.getFullYear(), actual.getMonth(), actual.getDate());

        let icon = '';
        let color = '';

        if (actualDate.getTime() === apptDate.getTime()) {
          icon = '✔';
          color = 'green';
        } else if (actualDate.getTime() < apptDate.getTime()) {
          icon = '⚠';
          color = 'orange';
        } else {
          icon = '❌';
          color = 'red';
        }

        return `<span>${actualDay} <span style="color:${color}; font-weight:bold;">${icon}</span></span>`;
      }
    },

    { field: 'consignee_locality', headerName: 'Consignee City' },
    { field: 'consignee_region', headerName: 'Consignee Prov' },
    { field: 'consignee_contact_date', headerName: 'Date Store Was Called', filter: 'agDateColumnFilter' },

    {
      field: 'appointment_date',
      headerName: 'Appt Date',
      filter: 'agDateColumnFilter',
      valueGetter: p => this.formatDateString(p.data?.appointment_date)
    },

    { field: 'appointment_window', headerName: 'Appointment Window', filter: false },
    { field: 'delivery_status', headerName: 'On-Day?', cellRenderer: this.deliveryDayStatusFormatting },
    { field: 'lading_status', headerName: 'Status' },
    { field: 'operation_delays', headerName: 'Operation Delays', filter: false }
  ];

  lading_uuid: string = '';
  isStatusModalOpen: boolean = false;

  @ViewChild(LadingStatusModalComponent) lading_status_modal!: LadingStatusModalComponent;

  constructor(
    private http: HttpClient,
    private gridStateService: UserGridStateService,
    private zone: NgZone,
    private toastr: ToastrService
  ) {
    this.quickFilter$
      .pipe(debounceTime(180), distinctUntilChanged())
      .subscribe(text => {
        if (this.gridApi) {
          this.gridApi.setGridOption('quickFilterText', text);
        }
      });
  }

  ngOnInit() {
    const raw = localStorage.getItem('menuData');
    if (raw) {
      const menuData: MenuData = JSON.parse(raw);
      if (menuData.user.email?.toLowerCase() === 'allan@ulaap.com') {
        this.isAllan = true;
        console.log('Allan is signed in!');
      }
    }

    if (this.isAllan) {
      const moonCol: ColDef = {
        headerName: 'Moon Phase',
        field: 'local_time',
        filter: false,
        valueGetter: (p) => {
          const dateStr = p.data?.local_time;
          if (!dateStr) return '';
          return this.getMoonPhaseLabel(new Date(dateStr));
        }
      };

      const idx = this.columnDefs.findIndex(c => c.headerName === 'Actual Day of Week');
      const insertAt = idx >= 0 ? idx + 1 : this.columnDefs.length;

      this.columnDefs = [
        ...this.columnDefs.slice(0, insertAt),
        moonCol,
        ...this.columnDefs.slice(insertAt)
      ];

      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.columnDefs);
      }
    }

    this.http.get<any>(this.apiUrl).subscribe(
      response => {
        const data: StoreReport[] = response || [];
        this.rowData = data.map((row): StoreReportExtended => {
          const d = this.pickRefDate(row);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const week = (row as any).week_no ?? this.getWeekNumber(d);

          return {
            ...row,
            sort_week: `${year}${month}${String(week).padStart(2, '0')}`,
            chart_week: `${year}-${month}-W${String(week).padStart(2, '0')}`,
            distribution_center_reference: (row as any).distribution_center_reference ?? null,
            pool_point_reference_code: (row as any).pool_point_reference_code ?? null,
            lading_uuid: (row as any).lading_uuid ?? ''
          };
        });

        this.fetchAndApplyGridState();
      },
      error => {
        console.error('Error getting store report data:', error);
        this.toastr.error('Failed to load store reports.');
        this.gridReady = true;
      }
    );
  }

  onGridReady(params: GridReadyEvent<StoreReport>) {
    this.gridApi = params.api;

    this.gridApi.applyColumnState({
      state: [{ colId: 'week_no', sort: 'desc' }],
      defaultState: { sort: null }
    });
  }

  fetchAndApplyGridState() {
    this.gridStateService.getUserGridState('store_reports_client').subscribe(
      state => {
        if (state) this.initialState = state;
        this.gridReady = true;
      },
      error => {
        console.error('Failed to fetch grid state:', error);
        this.gridReady = true;
      }
    );
  }

  onStateUpdated(params: StateUpdatedEvent<StoreReport>) {
    if (
      params.sources.includes('columnSizing') ||
      params.sources.includes('columnOrder') ||
      params.sources.includes('filter')
    ) {
      this.gridStateService.saveUserGridState('store_reports_client', params.state);
    }
  }

  onQuickFilterInput(value: string) {
    this.quickFilter$.next(value);
  }

  toggleTheme() {
    this.theme = this.theme === 'ag-theme-ulaap' ? 'ag-theme-ulaap-dark' : 'ag-theme-ulaap';
  }

  exportToCSV() {
    this.gridApi.exportDataAsCsv();
  }

  exportToExcel() {
    this.gridApi.exportDataAsExcel();
  }

  downloadBestChart() {
    if (!this.rowData.length) return;

    const buckets: Record<string, number> = {};
    this.rowData.forEach(r => {
      if (!r.chart_week) return;
      const cartons = Number(r.total_cartons) || 0;
      buckets[r.chart_week] = (buckets[r.chart_week] || 0) + cartons;
    });

    const formatLabel = (key: string) => {
      const parts = key.split('-');
      const year = parts[0].slice(2);
      const week = parts[2];
      return `${year}-${week}`;
    };

    const data = Object.entries(buckets).map(([week, cartons], i) => ({
      week,
      weekLabel: formatLabel(week),
      cartons,
      index: i
    }));

    const chartOptions: AgCartesianChartOptions = {
      title: {
        text: 'Cartons per Week',
        fontSize: 18,
        fontWeight: 'bold'
      },
      data,
      series: [
        {
          type: 'bar',
          direction: 'vertical',
          xKey: 'weekLabel',
          yKey: 'cartons',
          strokeWidth: 0,
          itemStyler: ({ datum }) => {
            const idx = (datum as any).index;
            return {
              fill: idx % 2 === 0 ? '#2563eb' : '#3b82f6'
            };
          },
          tooltip: {
            renderer: ({ datum }) => ({
              title: datum.weekLabel,
              content: `${datum.cartons.toLocaleString()} cartons`
            })
          }
        } as AgBarSeriesOptions
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          label: {
            rotation: -45,
            fontSize: 11
          },
          title: { text: 'Week' },
          gridLine: { enabled: false }
        },
        {
          type: 'number',
          position: 'left',
          title: { text: 'Total Cartons' },
          gridLine: { enabled: true }
        }
      ],
      legend: { enabled: false },
      background: { fill: '#ffffff' }
    };

    const chartRef = AgCharts.create(chartOptions);
    (AgCharts as any).download(chartRef, { fileName: 'cartons_per_week', fileFormat: 'png' });
    chartRef.destroy();
  }

  openStatusModal(params: ICellRendererParams<StoreReportExtended>) {
    this.isStatusModalOpen = true;
    this.lading_uuid = params.data?.lading_uuid || '';
  }

  closeStatusModal(update_successful: any) {
    this.isStatusModalOpen = false;
    if (update_successful == 1) {
      this.toastr.success('Status successfully updated!');
    } else if (update_successful == 2) {
      this.toastr.error('Status was not updated.');
    }
  }

  openDeliveryReports() {
    window.open('/delivery_reports');
  }

  openExceptionReports() {
    window.open('/exception_reports');
  }

  private pickRefDate(row: any): Date {
    const ref = row?.load_start || row?.outbound_scan_date || row?.date;
    const d = new Date(ref);
    return isNaN(d.getTime()) ? new Date() : d;
  }

  private getWeekNumber(date: Date): number {
    const firstJan = new Date(date.getFullYear(), 0, 1);
    const pastDays = (date.getTime() - firstJan.getTime()) / 86400000;
    return Math.ceil((pastDays + firstJan.getDay() + 1) / 7);
  }

  private formatDateString(value: string | null): string {
    if (!value || isNaN(new Date(value).getTime())) return '';
    return value.split(' ')[0];
  }

  private formatTimeString(value: string | null): string {
    if (!value || isNaN(new Date(value).getTime())) return '';
    return new Date(value).toTimeString().split(' ')[0];
  }

  deliveryDayStatusFormatting(params: ICellRendererParams) {
    let statusTypes: string[] = [];
    if (params.value != null) {
      statusTypes = (params.value as string).split(',');
    }

    let html = '';
    for (let type of statusTypes) {
      let label = type.trim();
      let bg = '';

      if (label.toUpperCase().includes('LATE DATE')) {
        label = 'LATE DATE';
        bg = '#FF6347';
      } else if (label.toUpperCase().includes('EARLY DATE')) {
        label = 'EARLY DATE';
        bg = '#FFD700';
      } else if (label.toUpperCase().includes('ON DATE')) {
        label = 'ON DATE';
        bg = '#98FB98';
      }

      if (bg) {
        html += `<button class="pill" style="background-color:${bg}">${label}</button>`;
      }
    }

    return html || '';
  }

  private getMoonPhaseLabel(d: Date): string {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';

    const synodic = 29.530588853;
    const ref = Date.UTC(2000, 0, 6, 18, 14, 0);
    const daysSince = (d.getTime() - ref) / 86400000;
    const age = ((daysSince % synodic) + synodic) % synodic;

    if (age < 1.84566) return '🌑 New';
    if (age < 5.53699) return '🌒 Waxing Crescent';
    if (age < 9.22831) return '🌓 First Quarter';
    if (age < 12.91963) return '🌔 Waxing Gibbous';
    if (age < 16.61096) return '🌕 Full';
    if (age < 20.30228) return '🌖 Waning Gibbous';
    if (age < 23.99361) return '🌗 Last Quarter';
    return '🌘 Waning Crescent';
  }
}
